import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { EditTyreForm, ReplaceTyreConfirm } from "modules/IncompleteTyresDashboard/components/EditTyreModal/components";
import "modules/IncompleteTyresDashboard/components/EditTyreModal/EditTyreModal.scss";
import { useBrandsQuery, useCompleteTyreMutation } from "modules/IncompleteTyresDashboard/components/EditTyreModal/hooks";
import { EAN_VALIDATION, isKnownManufacturer } from "utils/common";

import type { Tyre } from "models";
import type { EditTyreFormValues } from "modules/IncompleteTyresDashboard/components/EditTyreModal/components/EditTyreForm";

type EditTyreModalProps = {
  tyre: Tyre;
  onClose: () => void;
};

const FormHelper = createFormHelper<EditTyreFormValues>();

export const EditTyreModal = ({ tyre, onClose }: EditTyreModalProps) => {
  const [replacementTyre, setReplacementTyre] = useState<Tyre | null>(null);

  const { data: brandOptions = [] } = useBrandsQuery();
  const { mutateAsync: completeTyre, isPending: isCompleteTyrePending } = useCompleteTyreMutation();

  const handleClose = () => {
    setReplacementTyre(null);
    onClose();
  };

  const isFormInvalid = (values: EditTyreFormValues) =>
    !values.manufacturer ||
    !isKnownManufacturer(brandOptions, values.manufacturer) ||
    !values.description ||
    !values.width ||
    !(parseFloat(values.size) > 0) ||
    !values.speed ||
    !values.height ||
    !values.carry_capacity ||
    !EAN_VALIDATION.test(values.ean);

  const handleSubmit = async (values: EditTyreFormValues) => {
    if (isFormInvalid(values)) return;

    await completeTyre({ ...values, size: parseFloat(values.size) || 0 });
    handleClose();
  };

  return (
    <>
      <Modal open={true} onClose={handleClose} closeOnDimmerClick={false} size="large">
        <div className="EditTyreModal">
          <FormHelper.Form
            onSubmit={handleSubmit}
            defaultValues={{
              id: tyre.id,
              manufacturer: tyre.manufacturer,
              description: tyre.description,
              carry_capacity: tyre.carry_capacity,
              width: tyre.width,
              height: tyre.height,
              size: String(tyre.size),
              speed: tyre.speed,
              season: tyre.season,
              ean: tyre.ean ?? "",
              run_flat_tire: tyre.run_flat_tire
            }}
          >
            <EditTyreForm brandOptions={brandOptions} isPending={isCompleteTyrePending} onClose={handleClose} onTyreReplace={tyre => setReplacementTyre(tyre)} />
          </FormHelper.Form>
        </div>
      </Modal>

      <ReplaceTyreConfirm tyre={tyre} replacementTyre={replacementTyre} onClose={() => setReplacementTyre(null)} onReplaced={handleClose} />
    </>
  );
};
